import React, { useState } from "react";
import { Modal } from "../common/Modal";
import {
  Opportunity,
  ProposalAddendum,
  Requirement,
  AddendumAcknowledgmentStatus,
  MilestonePresets,
} from "../../types";
import { Loader2, Calendar, FileText, CheckCircle2, UploadCloud } from "lucide-react";
import { uploadRfpDocument } from "../../services/storageService";

interface AddendumModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: Opportunity;
  addendum?: ProposalAddendum | null;
  requirements: Requirement[];
  milestonePresets?: MilestonePresets;
  onSave: (
    data: Omit<ProposalAddendum, "id" | "opportunityId" | "createdAt" | "updatedAt">,
    autoShiftDates: boolean,
  ) => Promise<void>;
}

interface AddendumFormProps {
  opportunity: Opportunity;
  addendum?: ProposalAddendum | null;
  requirements: Requirement[];
  onClose: () => void;
  onSave: (
    data: Omit<ProposalAddendum, "id" | "opportunityId" | "createdAt" | "updatedAt">,
    autoShiftDates: boolean,
  ) => Promise<void>;
}

const AddendumForm: React.FC<AddendumFormProps> = ({
  opportunity,
  addendum,
  requirements,
  onClose,
  onSave,
}) => {
  const [number, setNumber] = useState(addendum?.number || "");
  const [title, setTitle] = useState(addendum?.title || "");
  const [issueDate, setIssueDate] = useState(
    addendum?.issueDate || new Date().toISOString().slice(0, 10),
  );
  const [summary, setSummary] = useState(addendum?.summary || "");

  // Deadlines
  const [shiftsSubmissionDeadline, setShiftsSubmissionDeadline] = useState(
    addendum ? addendum.shiftsSubmissionDeadline : false,
  );
  const [newSubmissionDate, setNewSubmissionDate] = useState(
    addendum?.newSubmissionDate || opportunity.dueDate || "",
  );
  const [newSubmissionTime, setNewSubmissionTime] = useState(
    addendum?.newSubmissionTime || opportunity.dueTime || "17:00",
  );

  const [shiftsQuestionsDeadline, setShiftsQuestionsDeadline] = useState(
    addendum ? addendum.shiftsQuestionsDeadline : false,
  );
  const [newQuestionsDate, setNewQuestionsDate] = useState(
    addendum?.newQuestionsDate || opportunity.questionsDueDate || "",
  );

  const [autoShiftMilestones, setAutoShiftMilestones] = useState(true);

  // Acknowledgment
  const [acknowledgmentRequired, setAcknowledgmentRequired] = useState(
    addendum ? addendum.acknowledgmentRequired : true,
  );
  const [acknowledgmentStatus, setAcknowledgmentStatus] = useState<AddendumAcknowledgmentStatus>(
    addendum?.acknowledgmentStatus || "pending_acknowledgment",
  );

  // Impacted Requirements
  const [selectedReqIds, setSelectedReqIds] = useState<string[]>(
    addendum?.affectedRequirementIds || [],
  );

  // Attachment upload
  const [documentPath, setDocumentPath] = useState(addendum?.documentPath || "");
  const [documentName, setDocumentName] = useState(addendum?.documentName || "");
  const [isUploading, setIsUploading] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setErrorMessage(null);
    try {
      const uploadRes = await uploadRfpDocument(opportunity.id, file);
      setDocumentPath(uploadRes.storagePath);
      setDocumentName(file.name);
    } catch (err) {
      console.error("Failed to upload addendum file:", err);
      setErrorMessage(err instanceof Error ? err.message : "Failed to upload document.");
    } finally {
      setIsUploading(false);
    }
  };

  const toggleRequirement = (reqId: string) => {
    setSelectedReqIds((prev) =>
      prev.includes(reqId) ? prev.filter((id) => id !== reqId) : [...prev, reqId],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!number.trim()) {
      setErrorMessage("Please enter an addendum number (e.g. Addendum #1).");
      return;
    }
    if (!title.trim()) {
      setErrorMessage("Please enter a title or subject.");
      return;
    }
    if (!issueDate) {
      setErrorMessage("Please select an issue date.");
      return;
    }
    if (!summary.trim()) {
      setErrorMessage("Please provide a summary of what changed.");
      return;
    }
    if (shiftsSubmissionDeadline && !newSubmissionDate) {
      setErrorMessage("Please enter the new proposal submission due date.");
      return;
    }
    if (shiftsQuestionsDeadline && !newQuestionsDate) {
      setErrorMessage("Please enter the new questions due date.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await onSave(
        {
          number: number.trim(),
          title: title.trim(),
          issueDate,
          summary: summary.trim(),
          shiftsSubmissionDeadline,
          newSubmissionDate: shiftsSubmissionDeadline ? newSubmissionDate : undefined,
          newSubmissionTime: shiftsSubmissionDeadline ? newSubmissionTime : undefined,
          shiftsQuestionsDeadline,
          newQuestionsDate: shiftsQuestionsDeadline ? newQuestionsDate : undefined,
          acknowledgmentRequired,
          acknowledgmentStatus: acknowledgmentRequired ? acknowledgmentStatus : "not_required",
          affectedRequirementIds: selectedReqIds,
          documentPath: documentPath || undefined,
          documentName: documentName || undefined,
          createdByEmail: addendum?.createdByEmail || "",
        },
        autoShiftMilestones,
      );
      onClose();
    } catch (err) {
      console.error("Failed to save addendum:", err);
      setErrorMessage(err instanceof Error ? err.message : "Failed to save addendum.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errorMessage && (
        <div className="rounded-lg bg-rose-50 p-2.5 text-xs text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
          {errorMessage}
        </div>
      )}

      {/* Row 1: Number, Date, Title */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label
            htmlFor="addendum-number"
            className="block text-xs font-semibold text-slate-700 dark:text-slate-300"
          >
            Addendum / Amendment # *
          </label>
          <input
            id="addendum-number"
            type="text"
            required
            placeholder="e.g. Addendum #1"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
        </div>

        <div>
          <label
            htmlFor="addendum-date"
            className="block text-xs font-semibold text-slate-700 dark:text-slate-300"
          >
            Issue Date *
          </label>
          <input
            id="addendum-date"
            type="date"
            required
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
        </div>

        <div>
          <label
            htmlFor="addendum-title"
            className="block text-xs font-semibold text-slate-700 dark:text-slate-300"
          >
            Title / Subject *
          </label>
          <input
            id="addendum-title"
            type="text"
            required
            placeholder="e.g. Pre-Bid Q&A & Extension"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
        </div>
      </div>

      {/* Summary of Changes */}
      <div>
        <label
          htmlFor="addendum-summary"
          className="block text-xs font-semibold text-slate-700 dark:text-slate-300"
        >
          Summary of Modifications & Answers *
        </label>
        <textarea
          id="addendum-summary"
          rows={3}
          required
          placeholder="Summarize buyer responses, modified volume assumptions, revised technical specs, or payment terms..."
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
        />
      </div>

      {/* Deadline Extension Box */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 space-y-3 dark:border-slate-800 dark:bg-slate-900/40">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[#2ba5b5]" />
          <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">
            Schedule & Deadline Adjustments
          </h4>
        </div>

        <div className="space-y-2.5">
          <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={shiftsSubmissionDeadline}
              onChange={(e) => setShiftsSubmissionDeadline(e.target.checked)}
              className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5] dark:border-slate-700"
            />
            <span className="font-medium">Extends Proposal Submission Deadline</span>
          </label>

          {shiftsSubmissionDeadline && (
            <div className="ml-6 grid gap-3 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="new-submission-date"
                  className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400"
                >
                  New Submission Date *
                </label>
                <input
                  id="new-submission-date"
                  type="date"
                  required
                  value={newSubmissionDate}
                  onChange={(e) => setNewSubmissionDate(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
              </div>

              <div>
                <label
                  htmlFor="new-submission-time"
                  className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400"
                >
                  New Submission Time
                </label>
                <input
                  id="new-submission-time"
                  type="time"
                  value={newSubmissionTime}
                  onChange={(e) => setNewSubmissionTime(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={shiftsQuestionsDeadline}
              onChange={(e) => setShiftsQuestionsDeadline(e.target.checked)}
              className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5] dark:border-slate-700"
            />
            <span className="font-medium">Extends Vendor Questions Cutoff Date</span>
          </label>

          {shiftsQuestionsDeadline && (
            <div className="ml-6 sm:w-1/2">
              <label
                htmlFor="new-questions-date"
                className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400"
              >
                New Questions Due Date *
              </label>
              <input
                id="new-questions-date"
                type="date"
                required
                value={newQuestionsDate}
                onChange={(e) => setNewQuestionsDate(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
            </div>
          )}

          {shiftsSubmissionDeadline && (
            <div className="ml-6 pt-1">
              <label className="flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-400">
                <input
                  type="checkbox"
                  checked={autoShiftMilestones}
                  onChange={(e) => setAutoShiftMilestones(e.target.checked)}
                  className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5] dark:border-slate-700"
                />
                <span>
                  Automatically recompute internal milestone timeline based on the new due date
                </span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Acknowledgment Compliance Box */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 space-y-3 dark:border-slate-800 dark:bg-slate-900/40">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-amber-500" />
          <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">
            Acknowledgment Form Compliance
          </h4>
        </div>

        <div className="space-y-2.5">
          <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={acknowledgmentRequired}
              onChange={(e) => {
                setAcknowledgmentRequired(e.target.checked);
                if (!e.target.checked) setAcknowledgmentStatus("not_required");
                else if (acknowledgmentStatus === "not_required")
                  setAcknowledgmentStatus("pending_acknowledgment");
              }}
              className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5] dark:border-slate-700"
            />
            <span className="font-medium">
              Buyer requires a signed Addendum Acknowledgment form in submission package
            </span>
          </label>

          {acknowledgmentRequired && (
            <div className="ml-6 sm:w-1/2">
              <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400">
                Current Status
              </label>
              <select
                value={acknowledgmentStatus}
                onChange={(e) =>
                  setAcknowledgmentStatus(e.target.value as AddendumAcknowledgmentStatus)
                }
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              >
                <option value="pending_acknowledgment">
                  Pending Signed Form (Action Required)
                </option>
                <option value="acknowledged">Signed & Acknowledged</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Impacted Requirements Checklist */}
      <div>
        <div className="flex items-center justify-between pb-1">
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
            Impacted Requirements ({selectedReqIds.length} selected)
          </label>
          <span className="text-[11px] text-slate-500">
            Tag requirements affected by this addendum
          </span>
        </div>

        {requirements.length === 0 ? (
          <p className="text-xs text-slate-500 italic">No requirements in this proposal yet.</p>
        ) : (
          <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 divide-y divide-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:divide-slate-800">
            {requirements.map((req) => {
              const isChecked = selectedReqIds.includes(req.id);
              return (
                <label
                  key={req.id}
                  className="flex items-start gap-2 py-1.5 px-1 cursor-pointer hover:bg-slate-50 rounded dark:hover:bg-slate-900"
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleRequirement(req.id)}
                    className="mt-0.5 rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5] dark:border-slate-700"
                  />
                  <div className="text-xs">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {req.sectionRef ? `${req.sectionRef} ` : ""}
                      {req.title}
                    </span>
                    <p className="line-clamp-1 text-[11px] text-slate-500">{req.prompt}</p>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Attachment Upload */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
          Official Addendum Document (PDF / DOCX)
        </label>
        <div className="mt-1 flex items-center gap-3">
          <label className="btn-animate inline-flex items-center gap-1.5 cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
            <UploadCloud className="h-3.5 w-3.5 text-[#2ba5b5]" />
            <span>{isUploading ? "Uploading..." : "Upload File"}</span>
            <input
              type="file"
              accept=".pdf,.docx,.doc"
              onChange={handleFileUpload}
              disabled={isUploading}
              className="hidden"
            />
          </label>
          {documentName && (
            <span className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
              <FileText className="h-3.5 w-3.5 text-slate-400" />
              {documentName}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
        <button
          type="button"
          onClick={onClose}
          className="btn-animate rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || isUploading}
          className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238997] disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <span>{addendum ? "Save Changes" : "Log Addendum"}</span>
          )}
        </button>
      </div>
    </form>
  );
};

export const AddendumModal: React.FC<AddendumModalProps> = ({
  isOpen,
  onClose,
  opportunity,
  addendum,
  requirements,
  onSave,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={addendum ? `Edit ${addendum.number}` : "Log RFP Addendum / Amendment"}
      description="Track client amendments, update shifted proposal deadlines, and tag impacted requirements."
      maxWidth="xl"
    >
      {isOpen && (
        <AddendumForm
          key={addendum ? addendum.id : "new"}
          opportunity={opportunity}
          addendum={addendum}
          requirements={requirements}
          onClose={onClose}
          onSave={onSave}
        />
      )}
    </Modal>
  );
};
